import React, { useState, useEffect } from 'react';
import './LegalPayments.css';
import { getPendingInvoices, confirmInvoice, getLegalPaymentHistory } from './api';

const LegalPayments = () => {
    const [pendingInvoices, setPendingInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmingInvoice, setConfirmingInvoice] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Загрузка неоплаченных счетов
    const fetchPendingInvoices = async () => {
        setLoading(true);
        try {
            const data = await getPendingInvoices();
            if (data.success) {
                setPendingInvoices(data.invoices || []);
            }
        } catch (error) {
            console.error('Ошибка при загрузке счетов:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingInvoices();
    }, []);

    // Подтверждение оплаты
    const handleConfirmPayment = async (invoiceNumber) => {
        if (!window.confirm(`Подтвердить оплату счёта ${invoiceNumber}?`)) {
            return;
        }

        setConfirmingInvoice(invoiceNumber);
        try {
            const result = await confirmInvoice(invoiceNumber);
            if (result.success) {
                alert(`${result.message}\nПодписка до: ${result.subscriptionEndDate}`);
                // Удаляем подтверждённый счёт из списка
                setPendingInvoices(prev => prev.filter(inv => inv.invoiceNumber !== invoiceNumber));
            } else {
                alert(`Ошибка: ${result.error}`);
            }
        } catch (error) {
            alert(`Ошибка: ${error.error || error.message}`);
        } finally {
            setConfirmingInvoice(null);
        }
    };

    // Просмотр истории платежей
    const handleViewHistory = async (invoice) => {
        setSelectedInvoice(invoice);
        setHistoryLoading(true);
        try {
            const data = await getLegalPaymentHistory(invoice.id);
            if (data.success) {
                setPaymentHistory(data.payments || []);
            }
        } catch (error) {
            console.error('Ошибка при загрузке истории:', error);
            setPaymentHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Форматирование суммы
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'RUB',
            minimumFractionDigits: 0
        }).format(amount / 100);
    };

    // Получение названия тарифа
    const getPlanName = (planKey) => {
        const plans = {
            'pro_12_months': 'PRO на 12 месяцев',
        };
        return plans[planKey] || planKey;
    };

    // Получение статуса платежа
    const getStatusBadge = (status) => {
        const statuses = {
            'pending': { label: 'Ожидает оплаты', className: 'status-pending' },
            'paid': { label: 'Оплачен', className: 'status-paid' },
            'cancelled': { label: 'Отменён', className: 'status-cancelled' }
        };
        const s = statuses[status] || { label: status, className: '' };
        return <span className={`status-badge ${s.className}`}>{s.label}</span>;
    };

    return (
        <div className="legal-payments-container">
            <div className="legal-payments-header">
                <h2>Оплата юр. лиц</h2>
                <button onClick={fetchPendingInvoices} disabled={loading}>
                    {loading ? 'Загрузка...' : 'Обновить'}
                </button>
            </div>

            <h3>Неоплаченные счета ({pendingInvoices.length})</h3>

            {loading ? (
                <div className="loading">Загрузка счетов...</div>
            ) : pendingInvoices.length === 0 ? (
                <div className="empty-state">Нет неоплаченных счетов</div>
            ) : (
                <table className="invoices-table">
                    <thead>
                        <tr>
                            <th>№ счёта</th>
                            <th>Компания</th>
                            <th>ИНН</th>
                            <th>Email</th>
                            <th>Тариф</th>
                            <th>Сумма</th>
                            <th>Дата создания</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pendingInvoices.map((invoice) => (
                            <tr key={invoice.id}>
                                <td className="invoice-number">{invoice.invoiceNumber}</td>
                                <td>
                                    <div className="company-name">{invoice.companyName}</div>
                                    <div className="company-full-name">{invoice.fullCompanyName}</div>
                                </td>
                                <td>{invoice.inn}</td>
                                <td>{invoice.email}</td>
                                <td>{getPlanName(invoice.planKey)}</td>
                                <td className="amount">{formatAmount(invoice.amount)}</td>
                                <td>{formatDate(invoice.createdAt)}</td>
                                <td className="actions">
                                    <button
                                        className="confirm-btn"
                                        onClick={() => handleConfirmPayment(invoice.invoiceNumber)}
                                        disabled={confirmingInvoice === invoice.invoiceNumber}
                                    >
                                        {confirmingInvoice === invoice.invoiceNumber
                                            ? 'Подтверждение...'
                                            : 'Подтвердить оплату'}
                                    </button>
                                    <button
                                        className="history-btn"
                                        onClick={() => handleViewHistory(invoice)}
                                    >
                                        История
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Модалка с историей платежей */}
            {selectedInvoice && (
                <div className="modal" onClick={() => setSelectedInvoice(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>История платежей: {selectedInvoice.companyName}</h3>
                            <button className="close-btn" onClick={() => setSelectedInvoice(null)}>×</button>
                        </div>

                        <div className="company-info">
                            <p><strong>Полное название:</strong> {selectedInvoice.fullCompanyName}</p>
                            <p><strong>ИНН:</strong> {selectedInvoice.inn}</p>
                            <p><strong>Email:</strong> {selectedInvoice.email}</p>
                        </div>

                        {historyLoading ? (
                            <div className="loading">Загрузка истории...</div>
                        ) : paymentHistory.length === 0 ? (
                            <div className="empty-state">Нет истории платежей</div>
                        ) : (
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th>№ счёта</th>
                                        <th>Тариф</th>
                                        <th>Сумма</th>
                                        <th>Способ оплаты</th>
                                        <th>Статус</th>
                                        <th>Создан</th>
                                        <th>Оплачен</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentHistory.map((payment) => (
                                        <tr key={payment.id}>
                                            <td>{payment.invoiceNumber || '—'}</td>
                                            <td>{getPlanName(payment.planKey)}</td>
                                            <td className="amount">{formatAmount(payment.amount)}</td>
                                            <td>{payment.paymentMethod === 'invoice' ? 'По счёту' : 'Карта'}</td>
                                            <td>{getStatusBadge(payment.status)}</td>
                                            <td>{formatDate(payment.createdAt)}</td>
                                            <td>{formatDate(payment.paidAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LegalPayments;
